import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderOpen, Clock, CheckCircle, AlertCircle, Loader, Filter, Download, ChevronRight, RefreshCw, Archive, ArchiveRestore } from 'lucide-react';
import { graphqlClient } from '../services/graphqlClient';
import { Job, JobStatus, JobType } from '../graphql/types';
import { fetchAuthSession } from 'aws-amplify/auth';
import { ConfirmModal, PromptModal, InfoModal } from '../components/Modal';

const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                     !import.meta.env.VITE_COGNITO_USER_POOL_ID;

const DocIngestion = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<JobStatus | 'ALL'>('ALL');
  const [sortBy, setSortBy] = useState<'date' | 'status'>('date');
  const [currentPage, setCurrentPage] = useState(1);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [downloadingExports, setDownloadingExports] = useState<string | null>(null);
  const [resubmittingJob, setResubmittingJob] = useState<string | null>(null);
  // Toggle between active and archived views.
  const [viewArchived, setViewArchived] = useState(false);
  const [restoringJob, setRestoringJob] = useState<string | null>(null);

  // Modal state
  const [createJobModal, setCreateJobModal] = useState(false);
  const [confirmResubmit, setConfirmResubmit] = useState<Job | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<Job | null>(null);
  const [archivingJob, setArchivingJob] = useState<string | null>(null);
  const [infoModal, setInfoModal] = useState<{ title: string; message: string } | null>(null);
  
  const ITEMS_PER_PAGE = 10;

  const loadJobs = useCallback(async (page: number = 1, token?: string) => {
    if (isPreviewMode) {
      setJobs([
        {
          jobId: 'preview-job-1',
          name: 'Sample Document Processing Job',
          tenantId: 'preview-tenant',
          username: 'preview-user',
          cognitoSub: 'preview-sub',
          jobType: JobType.DD,
          loanCount: 1,
          uploadLocation: 's3://preview-bucket/preview-tenant/preview-sub/preview-job-1/',
          exportLocation: 's3://preview-export/preview-tenant/preview-sub/preview-job-1/',
          status: viewArchived ? JobStatus.ARCHIVED : JobStatus.COMPLETED,
          createdAt: new Date(Date.now() - 86400000).toISOString(),
          startedAt: new Date(Date.now() - 86000000).toISOString(),
          completedAt: new Date(Date.now() - 85000000).toISOString(),
          processedLoanCount: 1,
          failedLoanCount: 0,
        },
        {
          jobId: 'preview-job-2',
          name: 'Invoice Processing Batch',
          tenantId: 'preview-tenant',
          username: 'preview-user',
          cognitoSub: 'preview-sub',
          jobType: JobType.DD,
          loanCount: 1,
          uploadLocation: 's3://preview-bucket/preview-tenant/preview-sub/preview-job-2/',
          exportLocation: 's3://preview-export/preview-tenant/preview-sub/preview-job-2/',
          status: viewArchived ? JobStatus.ARCHIVED : JobStatus.DOCUMENT_PROCESSING,
          createdAt: new Date(Date.now() - 3600000).toISOString(),
          startedAt: new Date(Date.now() - 3500000).toISOString(),
          processedLoanCount: 0,
          failedLoanCount: 0,
        },
      ]);
      setHasNextPage(false);
      setLoading(false);
      return;
    }

    try {
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
      const username = session.tokens?.idToken?.payload['cognito:username'] as string || 'unknown';

      // DynamoDB applies the server-side filter AFTER the page limit, so a
      // response can come back with 0 items and still have a nextToken. Keep
      // chaining pages until we've either filled the requested page size or
      // exhausted the table. Bounded by a safety cap so a pathological filter
      // (e.g. the whole table is archived) can't turn this into a full scan.
      const MAX_CHAIN = 10;
      let collected: Job[] = [];
      let cursor: string | undefined = token;
      let finalToken: string | null = null;
      for (let i = 0; i < MAX_CHAIN; i++) {
        const result = await graphqlClient.queries.listJobsByUser(
          tenantId,
          username,
          ITEMS_PER_PAGE,
          cursor,
          viewArchived
        );
        collected = collected.concat(result.items);
        finalToken = result.nextToken || null;
        if (!finalToken || collected.length >= ITEMS_PER_PAGE) break;
        cursor = finalToken;
      }

      if (page === 1) {
        setJobs(collected);
      } else {
        setJobs(prev => [...prev, ...collected]);
      }

      setNextToken(finalToken);
      setHasNextPage(!!finalToken);
      setError(null);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [viewArchived]);

  useEffect(() => {
    loadJobs(1);
  }, [loadJobs]);

  useEffect(() => {
    if (isPreviewMode) return;

    const setupSubscription = async () => {
      try {
        const session = await fetchAuthSession();
        const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
        const username = session.tokens?.idToken?.payload['cognito:username'] as string || 'unknown';

        const sub = graphqlClient.subscriptions.onJobStatusChanged(tenantId, username);
        const subscription = (sub as any).subscribe({
          next: ({ data }: any) => {
            if (!data?.onJobStatusChanged) return;
            const updated = data.onJobStatusChanged as Job;
            const belongsInView = viewArchived
              ? updated.status === JobStatus.ARCHIVED
              : updated.status !== JobStatus.ARCHIVED;
            setJobs(prev => {
              if (!belongsInView) return prev.filter(j => j.jobId !== updated.jobId);
              const index = prev.findIndex(j => j.jobId === updated.jobId);
              if (index >= 0) {
                const next = [...prev];
                next[index] = updated;
                return next;
              }
              return [updated, ...prev];
            });
          },
          error: (err: any) => console.error('Subscription error:', err),
        });

        return () => subscription.unsubscribe();
      } catch (err) {
        console.error('Failed to setup subscription:', err);
      }
    };

    setupSubscription();
  }, [viewArchived]);

  const handleCreateJob = async () => {
    if (isPreviewMode) {
      navigate('/doc-ingestion-job/preview-new-job');
      return;
    }
    setCreateJobModal(true);
  };

  const handleCreateJobConfirm = async (jobName: string) => {
    setCreateJobModal(false);
    try {
      const result = await graphqlClient.mutations.createJob({ name: jobName, jobType: JobType.DD });
      navigate(`/doc-ingestion-job/${result.job.jobId}`, {
        state: {
          job: result.job,
          uploadCredentials: result.uploadCredentials,
          uploadBucket: result.uploadBucket,
          uploadPrefix: result.uploadPrefix,
        },
      });
    } catch (err) {
      console.error('Failed to create job:', err);
      setError('Failed to create job. Please try again.');
    }
  };

  const handleDownloadAllExports = async (job: Job) => {
    if (job.status !== JobStatus.COMPLETED) return;
    setDownloadingExports(job.jobId);
    try {
      if (isPreviewMode) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        setInfoModal({ title: 'Download Complete', message: `Downloaded all exports for Job ${job.jobId.slice(0, 8)}` });
      } else {
        console.log('Download all exports for job:', job.jobId);
        setInfoModal({ title: 'Coming Soon', message: 'Download functionality will be implemented in a future update.' });
      }
    } catch (err) {
      console.error('Failed to download exports:', err);
      setError('Failed to download exports. Please try again.');
    } finally {
      setDownloadingExports(null);
    }
  };

  const handleResubmitJob = async (job: Job) => {
    if (job.status !== JobStatus.FAILED) return;
    setConfirmResubmit(job);
  };

  const handleResubmitConfirm = async (job: Job) => {
    setConfirmResubmit(null);
    setResubmittingJob(job.jobId);
    try {
      if (isPreviewMode) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        setJobs(prev => prev.map(j => j.jobId === job.jobId ? { ...j, status: JobStatus.DOCUMENT_PROCESSING } : j));
      } else {
        await graphqlClient.mutations.submitJob({ jobId: job.jobId, isResubmission: true });
        await loadJobs(1);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to resubmit job:', err);
      setError('Failed to resubmit job. Please try again.');
    } finally {
      setResubmittingJob(null);
    }
  };

  const handleDeleteJob = (job: Job) => {
    setConfirmArchive(job);
  };

  const handleDeleteConfirm = async (job: Job) => {
    setConfirmArchive(null);
    setArchivingJob(job.jobId);
    try {
      if (isPreviewMode) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setJobs(prev => prev.filter(j => j.jobId !== job.jobId));
      } else {
        const session = await fetchAuthSession();
        const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
        await graphqlClient.mutations.archiveJob(job.jobId, tenantId);
        setJobs(prev => prev.filter(j => j.jobId !== job.jobId));
      }
    } catch (err) {
      console.error('Failed to archive job:', err);
      setError('Failed to archive job. Please try again.');
    } finally {
      setArchivingJob(null);
    }
  };

  /**
   * Restore an archived job. We infer a sensible target status from the job's
   * step statuses; the resolver will accept any JobStatus enum value.
   */
  const handleRestoreJob = async (job: Job) => {
    setRestoringJob(job.jobId);
    try {
      if (isPreviewMode) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setJobs(prev => prev.filter(j => j.jobId !== job.jobId));
        return;
      }
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';

      let target: JobStatus;
      if (
        job.documentProcessingStatus === 'COMPLETED' &&
        job.documentVerificationStatus === 'COMPLETED' &&
        job.investorGuidelineReviewStatus === 'COMPLETED'
      ) {
        target = JobStatus.COMPLETED;
      } else if (job.documentVerificationStatus === 'COMPLETED') {
        target = JobStatus.INVESTOR_GUIDELINE_REVIEW;
      } else if (job.documentProcessingStatus === 'COMPLETED') {
        target = JobStatus.DOCUMENT_VERIFICATION;
      } else {
        target = JobStatus.DOCUMENT_PROCESSING;
      }

      await graphqlClient.mutations.setJobStatus(job.jobId, tenantId, target);
      setJobs(prev => prev.filter(j => j.jobId !== job.jobId));
    } catch (err) {
      console.error('Failed to restore job:', err);
      setError('Failed to restore job. Please try again.');
    } finally {
      setRestoringJob(null);
    }
  };

  const handleLoadMore = () => {
    if (hasNextPage && !loading) {
      setCurrentPage(prev => prev + 1);
      loadJobs(currentPage + 1, nextToken || undefined);
    }
  };

  const getStatusIcon = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case JobStatus.DOCUMENT_PROCESSING:
      case JobStatus.DOCUMENT_VERIFICATION:
      case JobStatus.INVESTOR_GUIDELINE_REVIEW:
      case JobStatus.UNDERWRITING_GUIDELINE_REVIEW:
        return <Loader className="h-5 w-5 text-blue-500 animate-spin" />;
      case JobStatus.FAILED:
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: JobStatus) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case JobStatus.COMPLETED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case JobStatus.DOCUMENT_PROCESSING:
      case JobStatus.DOCUMENT_VERIFICATION:
      case JobStatus.INVESTOR_GUIDELINE_REVIEW:
      case JobStatus.UNDERWRITING_GUIDELINE_REVIEW:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case JobStatus.FAILED:
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredJobs = jobs
    // Active view: hide any archived rows. Archived view: only keep archived rows.
    .filter(job => (viewArchived ? job.status === JobStatus.ARCHIVED : job.status !== JobStatus.ARCHIVED))
    .filter(job => viewArchived || filterStatus === 'ALL' || job.status === filterStatus)
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return a.status.localeCompare(b.status);
    });

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center h-64">
        <Loader className="h-8 w-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">      {isPreviewMode && (
        <div className="mb-6 bg-blue-900/20 border border-blue-700/50 rounded-xl p-4 backdrop-blur-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-white">
                <strong>Preview Mode:</strong> Showing sample jobs. In the full application, 
                you would see your actual document processing jobs.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {viewArchived ? 'Archived Document Processing Jobs' : 'Document Processing Jobs'}
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              {viewArchived
                ? 'Jobs that have been archived. Restore one to return it to the active list.'
                : 'Manage and monitor your document processing jobs'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setViewArchived(v => !v)}
              className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                viewArchived
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title={viewArchived ? 'Show active jobs' : 'Show archived jobs'}
            >
              <Archive className="h-4 w-4 mr-2" />
              {viewArchived ? 'View Active Jobs' : 'View Archived Jobs'}
            </button>
            {!viewArchived && (
              <button
                onClick={handleCreateJob}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create New Job
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-700/50 rounded-xl p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-white">{error}</p>
          </div>
        </div>
      )}

      {/* Filters — hide the status filter in the archived view since every row is ARCHIVED. */}
      {!viewArchived && (
        <div className="mb-6 flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as JobStatus | 'ALL')}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value={JobStatus.DOCUMENT_PROCESSING}>Document Processing</option>
              <option value={JobStatus.DOCUMENT_VERIFICATION}>Document Verification</option>
              <option value={JobStatus.INVESTOR_GUIDELINE_REVIEW}>Guideline Review</option>
              <option value={JobStatus.COMPLETED}>Completed</option>
              <option value={JobStatus.FAILED}>Failed</option>
            </select>
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'status')}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500"
          >
            <option value="date">Sort by Date</option>
            <option value="status">Sort by Status</option>
          </select>
        </div>
      )}

      {filteredJobs.length === 0 ? (
        <div className="bg-white backdrop-blur-sm shadow-xl rounded-xl border border-gray-200 p-12 text-center">
          <FolderOpen className="mx-auto h-16 w-16 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {viewArchived ? 'No archived jobs' : 'No jobs found'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {viewArchived
              ? 'Jobs you archive will show up here.'
              : filterStatus === 'ALL'
              ? 'Get started by creating your first document processing job.'
              : `No jobs with status "${filterStatus}".`}
          </p>
          {!viewArchived && filterStatus === 'ALL' && (
            <button
              onClick={handleCreateJob}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create New Job
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <div
              key={job.jobId}
              className="bg-white backdrop-blur-sm shadow-xl rounded-xl border border-gray-200 p-6 hover:shadow-2xl hover:border-blue-500 transition-all"
            >
              <div className="flex items-start justify-between">
                <div 
                  className="flex items-start space-x-4 flex-1 cursor-pointer"
                  onClick={() => navigate(`/doc-ingestion-job/${job.jobId}`)}
                >
                  <div className="mt-1">
                    {getStatusIcon(job.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {job.name || `Job ${job.jobId.slice(0, 8)}`}
                      </h3>
                      <span className={getStatusBadge(job.status)}>
                        {job.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Job ID: {job.jobId.slice(0, 8)} | Name: "{job.name}" | Has name: {job.name ? 'Yes' : 'No'}
                    </p>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Loans</p>
                        <p className="font-medium text-gray-900">{job.loanCount || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Processed Loans</p>
                        <p className="font-medium text-gray-900">{job.processedLoanCount || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Failed Loans</p>
                        <p className="font-medium text-gray-900">{job.failedLoanCount || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Created</p>
                        <p className="font-medium text-gray-900">{formatDate(job.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                  {/* Download / Resubmit actions only apply to the active view */}
                  {!viewArchived && job.status === JobStatus.COMPLETED && job.processedLoanCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadAllExports(job);
                      }}
                      disabled={downloadingExports === job.jobId}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {downloadingExports === job.jobId ? (
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Download All
                    </button>
                  )}
                  {!viewArchived && job.status === JobStatus.FAILED && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResubmitJob(job);
                      }}
                      disabled={resubmittingJob === job.jobId}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {resubmittingJob === job.jobId ? (
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Resubmit Job
                    </button>
                  )}
                  {viewArchived ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestoreJob(job);
                      }}
                      disabled={restoringJob === job.jobId}
                      title="Restore job to the active list"
                      className="inline-flex items-center px-3 py-2 border border-blue-500 text-sm leading-4 font-medium rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {restoringJob === job.jobId ? (
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ArchiveRestore className="h-4 w-4 mr-2" />
                      )}
                      Restore
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteJob(job);
                      }}
                      disabled={archivingJob === job.jobId}
                      title="Archive job"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {archivingJob === job.jobId ? (
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Archive className="h-4 w-4 mr-2" />
                      )}
                      Archive Job
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {hasNextPage && (
            <div className="flex justify-center pt-6">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ChevronRight className="h-5 w-5 mr-2" />
                    Load More Jobs
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {createJobModal && (
        <PromptModal
          title="Create New Job"
          label="Job name"
          placeholder={`Job ${new Date().toLocaleDateString()}`}
          defaultValue={`Job ${new Date().toLocaleDateString()}`}
          confirmLabel="Create Job"
          onConfirm={handleCreateJobConfirm}
          onCancel={() => setCreateJobModal(false)}
        />
      )}
      {confirmResubmit && (
        <ConfirmModal
          title="Resubmit Job"
          message={`Are you sure you want to resubmit "${confirmResubmit.name || confirmResubmit.jobId.slice(0, 8)}"? This will retry processing only the failed files.`}
          confirmLabel="Resubmit"
          variant="danger"
          onConfirm={() => handleResubmitConfirm(confirmResubmit)}
          onCancel={() => setConfirmResubmit(null)}
        />
      )}
      {confirmArchive && (
        <ConfirmModal
          title="Archive Job"
          message={`Archive "${confirmArchive.name || confirmArchive.jobId.slice(0, 8)}"? It will be removed from your active jobs list. You can bring it back anytime from the "View Archived Jobs" view.`}
          confirmLabel="Archive"
          variant="default"
          onConfirm={() => handleDeleteConfirm(confirmArchive)}
          onCancel={() => setConfirmArchive(null)}
        />
      )}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal(null)}
        />
      )}
    </div>
  );
};

export default DocIngestion;